import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import { Add, Edit, Delete, PlayArrow, Pause, Image } from '@mui/icons-material';
import { inventoryService } from '../inventory/services/inventoryService';
import { allegroApiService } from '../shared/services/allegroApi';
import { useShops } from '../shared/contexts/ShopsContext';
import { useAuth } from '../shared/contexts/AuthContext';
import { useDebounce } from '../inventory/hooks/useDebounce';
import CustomPagination from '../shared/components/Pagination';
import LazyImage from '../shared/components/LazyImage';
import AddProductDialog from '../shared/components/AddProductDialog';
import EditProductDialog from '../shared/components/EditProductDialog';
import DeleteConfirmDialog from '../shared/components/DeleteConfirmDialog';
import QuantityEditDialog from '../shared/components/QuantityEditDialog';
import InventoryFilters from '../shared/components/InventoryFilters';
import InventoryTableHeader from '../shared/components/InventoryTableHeader';
import config from '../config/environment';
import { Product } from '../inventory/models/Product';
import { generateUUID } from '../shared/utils/uuid';

// Memoized table row component for better performance
const ProductTableRow = React.memo(React.forwardRef(({ 
  product, 
  shops, 
  toggleLoading, 
  onEdit, 
  onDelete, 
  onToggleShop, 
  onQuantityEdit,
  getStatusChip 
}, ref) => {
  return (
    <TableRow ref={ref} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {(() => {
            const imageUrl = product.getImageUrl();
            return (
              <LazyImage
                src={imageUrl}
                alt={product.name}
                imageId={product.imageId}
                sx={{ mr: 2 }}
              />
            );
          })()}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{product.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {product.id}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{product.externalId}</TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            variant="h6"
            color={
              product.quantity === 0 ? 'error' : 
              product.quantity === 1 ? 'warning' : 
              product.quantity === 2 ? 'primary' : 
              'inherit'
            }
            sx={{ 
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: 1,
                px: 1,
              },
            }}
            onClick={() => onQuantityEdit(product)}
          >
            {product.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onQuantityEdit(product)}
            sx={{ 
              ml: 1,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align="center">
        {getStatusChip(product)}
      </TableCell>
      {shops.map((shop, index) => {
        const shopEnabled = product.enabledSet?.find(enabled => enabled.shopId === shop.id);
        return (
          <TableCell key={shop.id} align="center">
            {shopEnabled ? (
              <IconButton
                onClick={() => onToggleShop(product, shop.id, !shopEnabled.enabled)}
                disabled={toggleLoading[`${product.id}-${shop.id}`]}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {toggleLoading[`${product.id}-${shop.id}`] ? (
                  <CircularProgress size={16} />
                ) : shopEnabled.enabled ? (
                  <PlayArrow sx={{ color: 'success.main' }} />
                ) : (
                  <Pause sx={{ color: 'error.main' }} />
                )}
              </IconButton>
            ) : (
              <Typography variant="caption" color="text.secondary">-</Typography>
            )}
          </TableCell>
        );
      })}
      <TableCell align="center">
        <IconButton
          color="primary"
          onClick={() => onEdit(product)}
          size="small"
          sx={{ mr: 1 }}
        >
          <Edit />
        </IconButton>
        <IconButton
          color="error"
          onClick={() => onDelete(product)}
          size="small"
        >
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}));

const Inventory = () => {
  // Infinite scroll state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Stały rozmiar strony dla infinite scroll
  const [totalItems, setTotalItems] = useState(0);
  const isLoadingProductsRef = useRef(false);
  
  // Get shops from context
  const { shops, loading: shopsLoading, error: shopsError } = useShops();
  const { isAuthenticated } = useAuth();

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });



  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, config.PERFORMANCE.DEBOUNCE_DELAY);

  // Debug: log search term changes
  useEffect(() => {
    console.log('🔍 Search term changed:', { searchTerm, debouncedSearchTerm });
  }, [searchTerm, debouncedSearchTerm]);

  // UI state
  const [toggleLoading, setToggleLoading] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, product: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [quantityEditDialog, setQuantityEditDialog] = useState({ open: false, product: null, value: '' });
  const [imagePreview, setImagePreview] = useState(null);

  const [editForm, setEditForm] = useState({
    name: '',
    purchasePrice: '',
    externalId: '',
    quantity: 0,
    enabledSet: [],
  });
  const [newProduct, setNewProduct] = useState({
    externalId: generateUUID(),
    purchasePrice: '',
    name: '',
    image: null,
    quantity: 0,
  });

  // Load products with infinite scroll
  const loadProducts = useCallback(async (isLoadMore = false) => {
    if (isLoadingProductsRef.current) {
      return;
    }
    
    try {
      isLoadingProductsRef.current = true;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const params = {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm || '',
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy: sortConfig.field,
        sortOrder: sortConfig.direction,
      };

      console.log('🔍 loadProducts called with params:', params);
      console.log('🔍 Current state:', { 
        currentPage, 
        pageSize, 
        debouncedSearchTerm, 
        statusFilter, 
        sortConfig 
      });

      const result = await inventoryService.getProducts(params);
      
      if (isLoadMore) {
        setProducts(prev => [...prev, ...result.products]);
      } else {
        setProducts(result.products);
      }
      
      setTotalItems(result.pagination.total);
      setHasMore(result.products.length === pageSize);
      setError(null);
    } catch (err) {
      setError('Błąd podczas ładowania produktów');
      console.error('Błąd ładowania produktów:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingProductsRef.current = false;
    }
  }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, sortConfig.field, sortConfig.direction]);

  // Load more products function
  const loadMoreProducts = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    setCurrentPage(prev => prev + 1);
  }, [hasMore, loadingMore]);

  // Intersection Observer for infinite scroll
  const observerRef = useRef();
  const lastProductRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreProducts();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loadMoreProducts]);

  // Effects - load products when dependencies change
  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
    setProducts([]);
    setHasMore(true);
    
    // Load products with new filters
    loadProducts();
  }, [debouncedSearchTerm, statusFilter, sortConfig]);

  // Load more when page changes
  useEffect(() => {
    if (currentPage > 1) {
      loadProducts(true);
    }
  }, [currentPage, loadProducts]);



  // Handle sorting
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Helper functions
  const getStatusChip = useCallback((product) => {
    const lowStockThreshold = 10;
    const outOfStockThreshold = 0;
    
    let status;
    if (product.quantity <= outOfStockThreshold) {
      status = 'out_of_stock';
    } else if (product.quantity <= lowStockThreshold) {
      status = 'low_stock';
    } else {
      status = 'available';
    }

    const statusConfig = {
      available: { label: 'Dostępny', color: 'success' },
      low_stock: { label: 'Niski stan', color: 'warning' },
      out_of_stock: { label: 'Brak w magazynie', color: 'error' },
    };

    const config = statusConfig[status];
    return <Chip label={config.label} color={config.color} size="small" />;
  }, []);

  // Memoized event handlers
  const handleEditProduct = useCallback((product) => {
    setEditForm({
      name: product.name,
      purchasePrice: product.purchasePrice,
      externalId: product.externalId,
      quantity: product.quantity,
      enabledSet: [...product.enabledSet],
    });
    setEditDialog({ open: true, product });
  }, []);

  const handleDeleteProduct = useCallback((product) => {
    setDeleteDialog({ open: true, product });
  }, []);

  const handleToggleShopInTable = useCallback(async (product, shopId, newEnabled) => {
    const toggleKey = `${product.id}-${shopId}`;
    
    if (toggleLoading[toggleKey]) {
      return;
    }

    try {
      setToggleLoading(prev => ({ ...prev, [toggleKey]: true }));

      if (newEnabled) {
        await allegroApiService.activateProductInShop(shopId, product.externalId);
      } else {
        await allegroApiService.deactivateProductInShop(shopId, product.externalId);
      }

      const updatedProduct = await inventoryService.updateProduct(product.id, {
        ...product.toApi(),
        enabledSet: product.enabledSet.map(shop =>
          shop.shopId === shopId ? { ...shop, enabled: newEnabled } : shop
        ),
      });
      
      setProducts(products.map(p => p.id === product.id ? updatedProduct : p));
    } catch (err) {
      console.error('Błąd podczas przełączania sklepu:', err);
      setError(`Błąd podczas ${newEnabled ? 'aktywacji' : 'dezaktywacji'} produktu w sklepie: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setToggleLoading(prev => ({ ...prev, [toggleKey]: false }));
    }
  }, [toggleLoading, products]);

  const handleQuantityModalOpen = useCallback((product) => {
    setQuantityEditDialog({ 
      open: true, 
      product, 
      value: product.quantity.toString() 
    });
  }, []);

  // Event handlers
  const handleAddProduct = async () => {
    try {
      // Sprawdź autoryzację przed dodaniem produktu
      const token = localStorage.getItem(config.STORAGE_KEYS.ACCESS_TOKEN);
      
      if (!token) {
        throw new Error('Nie jesteś zalogowany. Zaloguj się ponownie.');
      }
      
      await inventoryService.addProduct(newProduct);
      setAddDialog({ open: false });
      setNewProduct({ externalId: generateUUID(), purchasePrice: '', name: '', image: null, quantity: 0 });
      setImagePreview(null);
      loadProducts();
    } catch (err) {
      console.error('Błąd dodawania produktu:', err);
      
      let errorMessage = err.message;
      if (err.message.includes('403')) {
        errorMessage = 'Brak uprawnień. Sprawdź czy jesteś zalogowany i masz odpowiednie uprawnienia.';
      } else if (err.message.includes('401')) {
        errorMessage = 'Sesja wygasła. Zaloguj się ponownie.';
      }
      
      // Pokaż błąd użytkownikowi
      setError(errorMessage);
    }
  };

  const handleSaveEdit = async () => {
    try {
      // Aktualizuj podstawowe dane produktu (bez zdjęcia)
      const productDataWithoutImage = { ...editForm };
      delete productDataWithoutImage.image; // Usuń zdjęcie z danych produktu
      
      const updatedProduct = await inventoryService.updateProduct(editDialog.product.id, productDataWithoutImage);
      
      // Jeśli jest nowe zdjęcie, zaktualizuj je osobno przez endpoint /image
      if (editForm.image) {
        try {
          // Przygotuj dane zgodnie ze strukturą ProductImageDto
          const imageDto = {
            data: editForm.image.data,
            contentType: editForm.image.contentType
          };
          
          // Dodaj id zdjęcia jeśli istnieje
          if (editDialog.product.imageId) {
            imageDto.id = editDialog.product.imageId;
          }
          
          await allegroApiService.updateProductImage(editDialog.product.id, imageDto);
          console.log('Zdjęcie produktu zostało zaktualizowane');
        } catch (imageError) {
          console.error('Błąd podczas aktualizacji zdjęcia:', imageError);
          // Nie przerywamy całej operacji, tylko logujemy błąd
        }
      }
      
      setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
      setEditDialog({ open: false, product: null });
    } catch (err) {
      console.error('Błąd aktualizacji produktu:', err);
    }
  };

  const handleDeleteProductConfirm = async () => {
    try {
      await inventoryService.deleteProduct(deleteDialog.product.id);
      setProducts(products.filter(p => p.id !== deleteDialog.product.id));
      setDeleteDialog({ open: false, product: null });
    } catch (err) {
      console.error('Błąd usuwania produktu:', err);
    }
  };

  const handleQuantityUpdate = async (product, newQuantity) => {
    try {
      await allegroApiService.updateProductQuantity(product.externalId, newQuantity);
      
      setProducts(products.map(p => {
        if (p.id === product.id) {
          const updatedProduct = Product.fromApi({ ...p.toApi(), quantity: newQuantity });
          updatedProduct.updateQuantity(newQuantity);
          return updatedProduct;
        }
        return p;
      }));
    } catch (err) {
      console.error('Błąd podczas aktualizacji stanu magazynowego:', err);
    }
  };

  const handleQuantityModalSave = async () => {
    const newQuantity = parseInt(quantityEditDialog.value);
    if (!isNaN(newQuantity) && newQuantity >= 0) {
      await handleQuantityUpdate(quantityEditDialog.product, newQuantity);
    }
    setQuantityEditDialog({ open: false, product: null, value: '' });
  };

  const handleQuantityModalCancel = () => {
    setQuantityEditDialog({ open: false, product: null, value: '' });
  };

  if (loading && products.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Zarządzanie magazynem lokalnym
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialog({ open: true })}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          Dodaj produkt
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtry */}
      <InventoryFilters
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        statusFilter={statusFilter}
        onStatusChange={(e) => setStatusFilter(e.target.value)}
        totalItems={totalItems}
      />

      {/* Optimized Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <InventoryTableHeader
            sortConfig={sortConfig}
            onSort={handleSort}
            shops={shops}
          />
          <TableBody>
            {products.map((product, index) => (
              <ProductTableRow
                key={product.id}
                product={product}
                shops={shops}
                toggleLoading={toggleLoading}
                onEdit={handleEditProduct}
                onDelete={handleDeleteProduct}
                onToggleShop={handleToggleShopInTable}
                onQuantityEdit={handleQuantityModalOpen}
                getStatusChip={getStatusChip}
                ref={index === products.length - 1 ? lastProductRef : null}
              />
            ))}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={shops.length + 5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Load More Button */}
      {hasMore && !loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={loadMoreProducts}
            disabled={loadingMore}
          >
            Załaduj więcej ({products.length} z {totalItems || '?'})
          </Button>
        </Box>
      )}

      {/* Dialogs */}
      <AddProductDialog
        open={addDialog.open}
        onClose={() => {
          setAddDialog({ open: false });
          setImagePreview(null);
        }}
        onAdd={handleAddProduct}
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
      />

      <EditProductDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, product: null })}
        onSave={handleSaveEdit}
        editForm={editForm}
        setEditForm={setEditForm}
        product={editDialog.product}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, product: null })}
        onConfirm={handleDeleteProductConfirm}
        product={deleteDialog.product}
      />

      <QuantityEditDialog
        open={quantityEditDialog.open}
        onClose={handleQuantityModalCancel}
        onSave={handleQuantityModalSave}
        product={quantityEditDialog.product}
        value={quantityEditDialog.value}
        onChange={(e) => setQuantityEditDialog(prev => ({ ...prev, value: e.target.value }))}
      />
    </Box>
  );
};

export default Inventory; 